/**
 * A sent quotation, re-priced from a new comparison allocation — as a REVISION (Akhil,
 * 2026-09-17: "a user wants to change the price of an item from the comparison page … main
 * changes can only be done in the revision page").
 *
 * The comparison builds the same lines it would for a fresh quotation (`buildQuotationPayload`).
 * Against a DRAFT those replace the document wholesale; against a SENT quotation that is not
 * allowed — the customer holds it. So the built lines are diffed against the document's, keyed by
 * enquiry row, and only the lines whose supplier or cost changed are written, one by one, through
 * `update_quotation_line` — exactly the write the revision page makes for a hand re-price. The
 * revision page then issues the round as revision N+1 ("Apply the round"), which is the only
 * thing that moves the document.
 *
 * The sell follows the quotation's PREVAILING margin (`prevailingSellRatio`): the customer's
 * prices were set at a ratio over cost, and a new cost keeps that ratio rather than the
 * allocation's default margin, which may be a different number. A document with no priced line
 * to read a ratio from takes the built sell as it is.
 *
 * Added rows (a built row with no line on the document) are NOT written here: adding a line to a
 * sent quotation is the revision page's add flow, with its own numbering and description.
 */

import { prevailingSellRatio } from './quotationCommission'
import type { QuotationLinePayload } from './quotationFromAllocation'
import type { QuotationLine } from './types'

export interface RepriceRow {
  line_id: string
  cost: number
  sell?: number
  source?: string
  supplier?: string
  supplier_quotation?: string
  po?: { po_currency: string; po_currency_rate: number; conversion_rate: number }
}

const round2 = (n: number) => Math.round(n * 100) / 100
const differs = (a: number, b: number) => Math.abs(a - b) >= 0.005

export function revisionRepriceChanges(lines: readonly QuotationLine[], built: readonly QuotationLinePayload[]): RepriceRow[] {
  const byRow = new Map(lines.map((l) => [l.cust_sno, l]))
  const ratio = prevailingSellRatio(lines)
  const out: RepriceRow[] = []
  for (const row of built) {
    const line = byRow.get(row.enquiry_row_no)
    if (!line || row.cost_unit <= 0) continue
    // `QuotationLine` carries no supplier (D22), so "changed" is read off the cost and the PO
    // side — a different supplier at the same figure in the same currency is the one case this
    // cannot see, and for the customer's price that case changes nothing.
    const costChanged = differs(line.cost_unit.amount, row.cost_unit)
    const poChanged =
      (row.po_currency !== undefined && row.po_currency !== line.po_currency) ||
      (row.po_currency_rate !== undefined && differs(row.po_currency_rate, line.po_currency_rate))
    if (!costChanged && !poChanged) continue
    out.push({
      line_id: line.line_id,
      cost: row.cost_unit,
      sell: ratio === null ? row.sell_unit : round2(row.cost_unit * ratio),
      ...(row.source ? { source: row.source } : {}),
      ...(row.supplier ? { supplier: row.supplier } : {}),
      ...(row.supplier_quotation ? { supplier_quotation: row.supplier_quotation } : {}),
      ...(row.po_currency && row.po_currency_rate !== undefined
        ? { po: { po_currency: row.po_currency, po_currency_rate: row.po_currency_rate, conversion_rate: row.conversion_rate ?? 1 } }
        : {}),
    })
  }
  return out
}
